import { useContext, useState } from "react";
import {
  ChaseInvoicesResponseModel,
  OrganisationFileResponse,
  ContactEmailModel,
  EmailTemplateResponse,
} from "@attelas/shared";
import { createEmail, addEmailsToCc, replyEmail } from "../taskpane";
import { invariant } from "../utils/invariant";
import { useChaseInvoices } from "./useChaseInvoices";
import { useChaseInvoice } from "./useChaseInvoice";
import { useUserConfiguration } from "./useUserConfiguration";
import { ToastContext } from "../context/ToastContext";
import { DownloadOrganisationFileResult, useDownloadOrganisationFile } from "./useDownloadOrganisationFile";
import { useOutlook } from "./useOutlook";
import { useTaskpaneTelemetry } from "../telemetry/useTaskpaneTelemetry";
import { useEmailTemplates } from "./emailTemplates/useEmailTemplates";
import { useDraftEmailTemplate } from "./emailTemplates/useDraftEmailTemplate";
import { buildTemplateParameters } from "./emailTemplates/buildTemplateParameters";
import {
  DraftRecipientMode,
  DEFAULT_DRAFT_RECIPIENT_MODE,
  getOtherContactEmails,
  resolveDraftRecipients,
} from "../utils/contactRecipients";

type ChaseParams = {
  customerId?: string | null | undefined;
  invoiceIds?: string | string[];
  isNew?: boolean;
  contacts?: ContactEmailModel[];
};

type PreviousEmail = Required<ChaseInvoicesResponseModel>;

function invoiceIdsCount(invoiceIds?: string | string[]): number {
  if (invoiceIds == null) return 0;
  return Array.isArray(invoiceIds) ? invoiceIds.length : 1;
}

export function useHandleChase(params: ChaseParams, onChase?: () => void) {
  const { isInReadMode } = useOutlook();
  const { showToast } = useContext(ToastContext);
  const { trackReceivableChaseSucceeded, trackReceivableChaseFailed } = useTaskpaneTelemetry();
  const [isLoading, setIsLoading] = useState(false);
  const [previousEmail, setPreviousEmail] = useState<PreviousEmail | null>(null);
  const { userConfiguration } = useUserConfiguration();
  const { downloadOrganisationFile } = useDownloadOrganisationFile();
  const chaseInvoices = useChase({ ...params, previousEmail });
  const { emailTemplatesMetadata } = useEmailTemplates();
  const { draftEmailTemplate } = useDraftEmailTemplate();
  const { contacts } = params;

  const relatedInvoiceIds = (): string[] => {
    if (Array.isArray(params.invoiceIds)) return params.invoiceIds;
    if (params.invoiceIds) return [params.invoiceIds];
    return [];
  };

  const deliverChaseEmail = async ({
    emailContent,
    subject,
    emailTo,
    emailCc,
    attachments,
    organisationFiles,
  }: {
    emailContent: string;
    subject: string;
    emailTo: string[];
    emailCc: string[];
    attachments: PreviousEmail["attachments"];
    organisationFiles?: OrganisationFileResponse[];
  }) => {
    const signature = userConfiguration?.signature;
    const emailContentWithSignature = signature ? `${emailContent}${signature}` : emailContent;
    let finalAttachments = attachments;
    const font = userConfiguration?.emailFont || null;

    if (organisationFiles?.length) {
      const downloaded = await Promise.all(
        organisationFiles.map((f) => downloadOrganisationFile(f.id, f.fileName || "file", f.fileExtension || "pdf"))
      );
      const extra = downloaded.filter((x): x is DownloadOrganisationFileResult => x != null);
      finalAttachments = [
        ...finalAttachments,
        ...extra.map((x) => ({
          name: x.name,
          content: x.content,
          link: null,
          extension: x.extension,
        })),
      ];
    }

    if (isInReadMode) {
      await replyEmail(
        emailContent,
        finalAttachments.map((attachment) => ({
          name: attachment.name,
          content: attachment.content,
          link: attachment.link,
        })),
        font,
        false
      );
    } else {
      await createEmail({
        to: emailTo,
        cc: emailCc,
        title: subject,
        text: emailContentWithSignature,
        files: finalAttachments.map((attachment) => ({
          base64File: attachment.content || "",
          fileName: attachment.name,
        })),
        isRewrite: !!previousEmail,
        font,
      });
    }

    setPreviousEmail({
      subject,
      content: emailContent,
      emailTo,
      emailCC: emailCc,
      attachments: finalAttachments,
    });
  };

  const handleChase = async (
    toneId?: string,
    organisationFiles?: OrganisationFileResponse[],
    language?: string,
    recipientMode: DraftRecipientMode = DEFAULT_DRAFT_RECIPIENT_MODE
  ) => {
    const invoiceCount = invoiceIdsCount(params.invoiceIds);
    const chaseBaseProps = () => ({
      invoice_count: String(invoiceCount),
      is_read_mode: String(isInReadMode),
      has_previous_draft: String(!!previousEmail),
      is_new_invoice: String(!!params.isNew),
      has_organisation_attachments: String((organisationFiles?.length ?? 0) > 0),
    });

    try {
      setIsLoading(true);
      const result = await chaseInvoices(toneId, language);
      const { to: emailTo, cc: emailCc } = result.emailTo
        ? resolveDraftRecipients(result.emailTo, contacts, recipientMode, result.emailCC)
        : { to: previousEmail?.emailTo.filter(Boolean) ?? [], cc: previousEmail?.emailCC.filter(Boolean) ?? [] };
      const subject = result.subject ?? previousEmail?.subject ?? "";
      const attachments = result.attachments.length ? result.attachments : (previousEmail?.attachments ?? []);

      await deliverChaseEmail({
        emailContent: result.content,
        subject,
        emailTo,
        emailCc,
        attachments,
        organisationFiles,
      });
      trackReceivableChaseSucceeded(chaseBaseProps());
      onChase?.();
    } catch (e) {
      console.error(e);
      trackReceivableChaseFailed({
        ...chaseBaseProps(),
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDraftFromTemplate = async (
    template: EmailTemplateResponse,
    recipientMode: DraftRecipientMode = DEFAULT_DRAFT_RECIPIENT_MODE
  ) => {
    const invoiceCount = invoiceIdsCount(params.invoiceIds);
    const chaseBaseProps = () => ({
      invoice_count: String(invoiceCount),
      is_read_mode: String(isInReadMode),
      has_previous_draft: String(!!previousEmail),
      is_new_invoice: String(!!params.isNew),
      has_organisation_attachments: "false",
    });

    try {
      setIsLoading(true);
      const metadata = emailTemplatesMetadata?.find((item) => item.templateType === template.type);
      const templateParameters = buildTemplateParameters(metadata?.templateProperties ?? [], {
        relatedIds: relatedInvoiceIds(),
        customerId: params.customerId ?? undefined,
      });

      const result = await draftEmailTemplate({
        templateId: template.id,
        templateParameters,
      });

      if (!result) return;

      const { to: emailTo, cc: emailCc } = resolveDraftRecipients([], contacts, recipientMode);
      await deliverChaseEmail({
        emailContent: result.content,
        subject: result.subject ?? "",
        emailTo,
        emailCc,
        attachments: result.attachments,
      });
      trackReceivableChaseSucceeded(chaseBaseProps());
      onChase?.();
    } catch (e) {
      console.error(e);
      trackReceivableChaseFailed({
        ...chaseBaseProps(),
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addOtherContactsToCc = async () => {
    if (!contacts?.length) return;

    try {
      const added = await addEmailsToCc(getOtherContactEmails(contacts));
      if (added.length) {
        showToast("Other contacts added to CC", "success");
      } else {
        showToast("All contacts are already on the email", "info");
      }
    } catch (e) {
      console.error(e);
      showToast("Couldn't add contacts to CC, please try again", "error");
    }
  };

  return {
    handleChase,
    handleDraftFromTemplate,
    addOtherContactsToCc,
    isLoading,
    hasBeenChased: !!previousEmail,
    resetHasBeenChased: () => setPreviousEmail(null),
  };
}

function useChase(params: ChaseParams & { previousEmail?: PreviousEmail | null }) {
  const { customerId, invoiceIds, previousEmail, isNew } = params;
  const { showToast } = useContext(ToastContext);
  const { chaseInvoices } = useChaseInvoices();
  const { chaseInvoice } = useChaseInvoice(typeof invoiceIds === "string" ? invoiceIds : null, isNew);

  function getRewriteEmail(toneId?: string) {
    if (!toneId || !previousEmail) return undefined;

    return {
      subject: previousEmail.subject,
      content: previousEmail.content,
      toneId: toneId,
    };
  }

  return async (toneId?: string, language?: string) => {
    if (Array.isArray(invoiceIds)) {
      invariant(!!customerId, "customerId is required when passing multiple invoiceIds");

      const rewriteEmail = getRewriteEmail(toneId);
      const resp = await chaseInvoices({
        customerId,
        invoiceIds,
        rewriteEmail,
        language,
      });
      if (invoiceIds.length !== resp.attachments.length) {
        showToast(
          "Couldn't find one or more attachments required for this email. Please check the missing files",
          "warning"
        );
      }
      return resp;
    }

    const { attachments, ...response } = await chaseInvoice({ rewriteEmail: getRewriteEmail(toneId), language });
    return {
      ...response,
      attachments: attachments ?? [],
    };
  };
}
